import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { Diagnosis } from './Diagnosis';
import { AutopsyProtocolFormSkeleton } from './AutopsyProtocolFormSkeleton';
import {
  AsyncControlSelect,
  ControlSelect,
  Field,
  Checkbox,
  Textarea,
  RichTextarea
} from '../../../common/components/Form';
import { useAutopsyProtocol } from './useAutopsyProtocol';
import { useProtocol } from '../context';
import { Hr } from '../../../common/elements/Hr/Hr';
import { Colors } from '../../../assets';
import { FontSize, FontWeight, Spacing } from '../../../assets/GlobalStyle/Variables';
import { useSidebarContext } from '../../../common/components/Sidebar/SidebarContext';
import { PopUp } from '../../../common/components/PopUp';
import { dateHelper } from '../../../helpers/help_functions/date_helpers';
import PulseLoader from 'react-spinners/PulseLoader';

export const AutopsyProtocolForm: FC = (): JSX.Element => {
  const { protocol, loading } = useProtocol();
  const {
    fields,
    methods,
    options,
    onSubmit,
    togglePopup,
    message,
    handleDeactivate,
    handleActivate,
    handleLock,
    handleUnLock,
    handlePrint,
    handleUZIS,
    loadRuianOptions,
    loadRuianInitialOption,
    printing
  } = useAutopsyProtocol();
  const { isCollapsed } = useSidebarContext();

  const isNotAutopsied = methods.watch('is_not_autopsied');
  const isLocked = !!protocol?.is_locked || !!protocol?.is_deactivated;
  const autopsyDisabled = isLocked || isNotAutopsied;
  const isDeathDateUnknown = methods.watch('death_date_unknown');
  const isDeathTimeUnknown = methods.watch('death_time_unknown');

  if (loading) {
    return <AutopsyProtocolFormSkeleton />;
  }

  return (
    <>
      {message && <PopUp message={message.message} toggle={togglePopup} type={message.type} />}
      <FormProvider {...methods}>
        <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
          <Row>
            <Col xl={6} lg={12} md={12}>
              <Row className="align-items-center">
                <Col lg={12} md={12} className="d-flex align-items-center">
                  <StyledProtocolNumberLabel>Číslo protokolu</StyledProtocolNumberLabel>
                  <StyledProtocolNumber>{protocol?.protocol_number}</StyledProtocolNumber>
                  {protocol?.is_locked && (
                    <StyledLockedWrapper>
                      <StyledLockedBadge>🔒 Uzamčeno</StyledLockedBadge>
                      <StyledLockedDate>
                        Odesláno do pojišťovny dne:{' '}
                        {dateHelper.formatDateCZ(protocol.locked_date ?? '')}
                      </StyledLockedDate>
                    </StyledLockedWrapper>
                  )}
                  {protocol?.is_deactivated && (
                    <StyledDeactivatedBadge>⚠️ Deaktivováno</StyledDeactivatedBadge>
                  )}
                  <StyledNepitvanWrapper $redLabel={isLocked && isNotAutopsied}>
                    <Checkbox
                      id="is_not_autopsied"
                      name="is_not_autopsied"
                      label="Nepitván"
                      disabled={isLocked}
                    />
                  </StyledNepitvanWrapper>
                </Col>
              </Row>
              <Row>
                <Col xl={10} lg={10} md={12}>
                  <Field {...fields.autopsy_date} disabled={autopsyDisabled} />
                </Col>
              </Row>
              <Row>
                <Col xl={6} lg={6} md={12}>
                  <Field {...fields.autopsy_time} disabled={autopsyDisabled} />
                </Col>
              </Row>
              <Row>
                <Col xl={10} lg={10} md={12}>
                  <ControlSelect
                    {...fields.type_of_examination}
                    options={options.examinationType}
                    autoTransform={true}
                    disabled={autopsyDisabled}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={6} md={12}>
                  <CheckboxFieldWrapper>
                    <Field {...fields.confidentiality_waiver} disabled={autopsyDisabled} />
                  </CheckboxFieldWrapper>
                </Col>
                <Col lg={6} md={12}>
                  <CheckboxFieldWrapper>
                    <Field {...fields.is_interrupted} disabled={autopsyDisabled} />
                  </CheckboxFieldWrapper>
                </Col>
              </Row>
              <Row>
                <Col lg={6} md={12}>
                  <CheckboxFieldWrapper>
                    <Field {...fields.sent_side_effects_report} disabled={autopsyDisabled} />
                  </CheckboxFieldWrapper>
                </Col>
                <Col lg={3} md={12}>
                  <CheckboxFieldWrapper>
                    <Field {...fields.sent_to_NOR} disabled={autopsyDisabled} />
                  </CheckboxFieldWrapper>
                </Col>
                <Col lg={3} md={12}>
                  <CheckboxFieldWrapper>
                    <Field {...fields.sent_to_TBC} disabled={autopsyDisabled} />
                  </CheckboxFieldWrapper>
                </Col>
              </Row>
            </Col>
            <Col xl={6} lg={12} md={12}>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.applicant}
                    options={options.applicants}
                    autoTransform={true}
                    disabled={isLocked}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <StyledTable>
                    <StyledTableHead>
                      <th>
                        <td>Atribut</td>
                      </th>
                      <th>
                        <td>Hodnota</td>
                      </th>
                    </StyledTableHead>
                    <StyledTableBody>
                      <tr>
                        <td>Bločky</td>
                        <td>
                          <Field {...fields.blocks} disabled={autopsyDisabled} />
                        </td>
                      </tr>
                      <tr>
                        <td>Preparáty</td>
                        <td>
                          <Field {...fields.slides} disabled={autopsyDisabled} />
                        </td>
                      </tr>
                    </StyledTableBody>
                  </StyledTable>
                </Col>
              </Row>
            </Col>
          </Row>
          <Hr />
          <Row>
            <Col xl={6} lg={6} md={12}>
              <Row>
                <Col lg={8} md={12}>
                  <Field {...fields.date_of_death} disabled />
                </Col>
                <Col lg={4} md={12}>
                  <Checkbox
                    id="death_date_unknown"
                    name="death_date_unknown"
                    label="Neuvedeno"
                    disabled={isLocked}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={8} md={12}>
                  <Field {...fields.time_of_death} disabled={isLocked || isDeathTimeUnknown} />
                </Col>
                <Col lg={4} md={12} className="d-flex flex-column justify-content-center gap-2">
                  <Checkbox
                    id="death_time_unknown"
                    name="death_time_unknown"
                    label="Neuvedeno"
                    disabled={isLocked}
                  />
                  <Checkbox
                    id="is_death_time_estimate"
                    name="is_death_time_estimate"
                    label={fields.is_death_time_estimate.label}
                    disabled={isLocked}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={10} md={12}>
                  <AsyncControlSelect
                    {...fields.place_of_death_ruian}
                    loadOptions={loadRuianOptions}
                    loadInitialOption={loadRuianInitialOption}
                    placeholder="Hledat adresu..."
                    disabled={isLocked}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={10} md={12}>
                  <Textarea {...fields.place_of_death} disabled={isLocked} />
                </Col>
              </Row>

              <Row>
                <Col lg={10} md={12}>
                  <Textarea {...fields.cause_of_death_autopsy} disabled={autopsyDisabled} />
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={12}>
                  <Checkbox
                    id="is_stillborn"
                    name="is_stillborn"
                    label={fields.is_stillborn.label}
                    disabled={isLocked}
                  />
                </Col>
                <Col lg={6} md={12}>
                  <Checkbox
                    id="drug_related_death"
                    name="drug_related_death"
                    label={fields.drug_related_death.label}
                    disabled={isLocked}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={12} md={12}>
                  <Checkbox
                    id="precious_metal_implants"
                    name="precious_metal_implants"
                    label={fields.precious_metal_implants.label}
                    disabled={isLocked}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={12} md={12}>
                  <Textarea {...fields.precious_metal_implants_note} disabled={isLocked} />
                </Col>
              </Row>

              <Hr />
              <Diagnosis disabled={autopsyDisabled} />
              <Hr />
              <Row>
                <Col md={12}>
                  <Field {...fields.note} disabled={isLocked} />
                </Col>
              </Row>
            </Col>
            <Col xl={6} lg={6} md={12}>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.examining_physician}
                    options={options.examiningPhysicians}
                    autoTransform={true}
                    disabled={isLocked}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Field {...fields.examining_physician_text} disabled={isLocked} />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.pathologist1}
                    options={options.employees}
                    autoTransform={true}
                    disabled={autopsyDisabled}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.pathologist2}
                    options={options.employees}
                    autoTransform={true}
                    disabled={autopsyDisabled}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.created_by}
                    options={options.employees}
                    autoTransform={true}
                    disabled={isLocked}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.lab_technician}
                    options={options.employees}
                    autoTransform={true}
                    disabled={autopsyDisabled}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Field {...fields.attendees} disabled={isLocked} />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <ControlSelect
                    {...fields.classification}
                    options={options.deathClassifications}
                    autoTransform={true}
                    disabled={isLocked}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <StyledAutopsyDiagnosisWrapper>
                    <StyledDisabledOverlay $isDisabled={autopsyDisabled}>
                      <RichTextarea {...fields.autopsy_diagnosis} disabled={autopsyDisabled} />
                    </StyledDisabledOverlay>
                    {!isLocked && (
                      <StyledAutopsyDiagnosisButtons>
                        {/* <StyledSmallPrimaryButton type="button">Editor</StyledSmallPrimaryButton> */}
                        <StyledSmallPrimaryButton type="button">
                          Vložit ze schránky
                        </StyledSmallPrimaryButton>
                      </StyledAutopsyDiagnosisButtons>
                    )}
                  </StyledAutopsyDiagnosisWrapper>
                </Col>
              </Row>
            </Col>
          </Row>
          <Hr />
          <Row>
            <Col md={12}>
              <StyledActionButtonsWrapper $isCollapsed={isCollapsed}>
                <StyledFlexWrapper>
                  <StyledOutlineButton type="button" onClick={handlePrint}>
                    {!printing ? (
                      'Tisk'
                    ) : (
                      <PulseLoader className="pulse-loader" size={6} color="#3c1149" />
                    )}
                  </StyledOutlineButton>
                  <StyledUzisWrapper>
                    <StyledOutlineButton type="button" onClick={handleUZIS}>
                      ÚZIS
                    </StyledOutlineButton>
                    {protocol?.sent_to_uzis_at && (
                      <StyledUzisSentDate>
                        Odesláno do ÚZIS: {dateHelper.formatDateCZ(protocol.sent_to_uzis_at)}
                      </StyledUzisSentDate>
                    )}
                  </StyledUzisWrapper>
                  {!protocol?.is_deactivated && (
                    <StyledOutlineButton type="button" onClick={handleDeactivate}>
                      Deaktivovat protokol
                    </StyledOutlineButton>
                  )}
                  {protocol?.is_deactivated && (
                    <StyledOutlineButton type="button" onClick={handleActivate}>
                      Aktivovat protokol
                    </StyledOutlineButton>
                  )}
                </StyledFlexWrapper>

                <StyledFlexWrapper>
                  {!protocol?.is_locked && !protocol?.is_deactivated ? (
                    <StyledOutlineButton type="button" onClick={handleLock}>
                      Uzamknout
                    </StyledOutlineButton>
                  ) : protocol?.is_locked ? (
                    <StyledOutlineButton type="button" onClick={handleUnLock}>
                      Odemknout
                    </StyledOutlineButton>
                  ) : (
                    ''
                  )}
                  {!protocol?.is_locked && (
                    <StyledPrimaryButton type="submit">Uložit</StyledPrimaryButton>
                  )}
                </StyledFlexWrapper>
              </StyledActionButtonsWrapper>
            </Col>
          </Row>
        </StyledForm>
      </FormProvider>
    </>
  );
};

const StyledFlexWrapper = styled.div`
  display: flex;
  gap: 1rem;
`;

const StyledActionButtonsWrapper = styled(StyledFlexWrapper)<{ $isCollapsed: boolean }>`
  justify-content: space-between;
  width: auto;
  position: fixed;
  bottom: 0;
  left: ${(props) => (props.$isCollapsed ? '59px' : '178px')};
  right: 0;
  background-color: #ffffffec;
  padding: 1rem;
  border-top: 2px solid ${Colors.lightGrey};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1;
  transition: left 0.3s ease;
`;

const StyledAutopsyDiagnosisWrapper = styled.div`
  position: relative;
  margin-top: 1rem;
  div:first-of-type {
    flex-direction: column;
    gap: 1rem;
  }

  label {
    display: block;
    min-width: auto;
  }
`;

const StyledAutopsyDiagnosisButtons = styled(StyledFlexWrapper)`
  position: absolute;
  right: 0;
  top: -5px;
`;

const StyledPrimaryButton = styled.button`
  background-color: ${Colors.primary};
  color: #fff;
  border: 1px solid ${Colors.primary};
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${Colors.secondary};
  }
`;

const StyledSmallPrimaryButton = styled(StyledPrimaryButton)`
  border: none;
  padding: 8px 16px;
  border-radius: 2px;
  white-space: nowrap;
  height: 30px;

  &:hover {
    background-color: ${Colors.secondary};
  }
`;

const StyledOutlineButton = styled(StyledPrimaryButton)`
  background-color: #fff;
  color: ${Colors.primary};
  border: 1px solid ${Colors.primary};

  &:hover {
    background-color: ${Colors.disabledHover};
  }
`;

const StyledForm = styled.form`
  padding-top: 1.5rem;
  padding-bottom: 100px;
`;

const StyledProtocolNumber = styled.span`
  margin: 0;
`;

const StyledProtocolNumberLabel = styled.p`
  margin: 0;
  color: ${Colors.grey};
  font-weight: ${FontWeight.strong};
  font-size: ${FontSize.normal};
  white-space: nowrap;
  min-width: 200px;
`;

const StyledLockedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
`;

const StyledLockedDate = styled.span`
  font-size: ${FontSize.medium};
  color: ${Colors.grey};
  white-space: nowrap;
`;

const StyledLockedBadge = styled.span`
  padding: 4px 12px;
  background-color: #ffc107;
  color: #000;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const StyledDeactivatedBadge = styled.span`
  margin-left: 12px;
  padding: 4px 12px;
  background-color: #dc3545;
  color: #fff;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const StyledTable = styled.table`
  width: 100%;
  margin-top: ${Spacing(4)};
  font-size: 14px;
`;

const StyledTableHead = styled.thead`
  background-color: ${Colors.lightBackground};
  border: 1px solid #cde0ee;
  height: 50px;
  vertical-align: middle;

  th {
    border-right: 1px solid ${Colors.lightGrey};
    padding: ${Spacing(4)};
  }
`;

const StyledTableBody = styled.tbody`
  border-left: 1px solid ${Colors.lightGrey};

  tr {
    height: 40px;
    border-bottom: 1px solid ${Colors.lightGrey};
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  td {
    border-right: 1px solid ${Colors.lightGrey};
    padding: ${Spacing(1)} ${Spacing(4)};

    > div:first-child {
      margin: 8px 0;
    }
  }
`;

const CheckboxFieldWrapper = styled.div`
  input[type='checkbox'] {
    height: 18px;
    width: 18px;
    cursor: pointer;
  }

  label {
    min-width: min-content;
    margin-right: ${Spacing(8)};
  }
`;

const StyledUzisWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const StyledUzisSentDate = styled.span`
  font-size: ${FontSize.medium};
  color: ${Colors.grey};
  white-space: nowrap;
`;

const StyledNepitvanWrapper = styled.div<{ $redLabel: boolean }>`
  margin-left: auto;
  label {
    color: ${({ $redLabel }) => ($redLabel ? Colors.error : Colors.grey)};
    font-weight: ${({ $redLabel }) => ($redLabel ? FontWeight.strong : FontWeight.normal)};
  }
`;

const StyledDisabledOverlay = styled.div<{ $isDisabled: boolean }>`
  opacity: ${({ $isDisabled }) => ($isDisabled ? '0.5' : '1')};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
`;
